/**
 * Sessiestatus in de frontend (US-001, US-002, US-003).
 *
 * Bij het wisselen van bedrijf wordt de volledige sessiestatus vervangen door het antwoord van de
 * server. Schermen lezen hun gegevens per bedrijf, zodat na een wissel geen gegevens van het
 * vorige bedrijf blijven staan (US-003, laatste criterium).
 */

import type { CompanyMembership, Role, SessionInfo } from '@prijsmonitor/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../api/client.js';

interface SessionState {
  status: 'laden' | 'ingelogd' | 'uitgelogd';
  session: SessionInfo | null;
  /** Het gekozen bedrijf, of null zolang er nog niets is gekozen. */
  activeCompany: CompanyMembership | null;
  /** De rol binnen het gekozen bedrijf; stuurt navigatie en actieknoppen (US-002/030). */
  role: Role | null;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  chooseCompany: (companyId: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [status, setStatus] = useState<SessionState['status']>('laden');

  const refresh = useCallback(async () => {
    try {
      setSession(await api.get<SessionInfo>('/api/session'));
      setStatus('ingelogd');
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        setSession(null);
        setStatus('uitgelogd');
        return;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const chooseCompany = useCallback(async (companyId: string) => {
    setSession(await api.post<SessionInfo>('/api/session/company', { companyId }));
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setSession(null);
    setStatus('uitgelogd');
  }, []);

  const value = useMemo<SessionState>(() => {
    const activeCompany =
      session?.memberships.find((m) => m.companyId === session.activeCompanyId) ?? null;
    return {
      status,
      session,
      activeCompany,
      role: activeCompany?.role ?? null,
      isAdmin: activeCompany?.role === 'ADMIN',
      refresh,
      chooseCompany,
      logout,
    };
  }, [session, status, refresh, chooseCompany, logout]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession moet binnen een SessionProvider worden gebruikt.');
  return context;
}
